import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { materialConsumptionApi, stockApi } from '../../api/client';
import { MaterialConsumption, StockItem } from '../../types';
import { History, Search, X, Package, FolderKanban, TrendingDown, Filter } from 'lucide-react';

// ─── Design tokens ────────────────────────────────────────────────────────────

const C = {
  bg: '#0a0a0f',
  card: '#12121a',
  border: '#1e1e2e',
  textPrimary: '#e2e1f0',
  textSecondary: '#6b6880',
  textMuted: '#4a4858',
  orange: '#f97316',
  orangeHover: '#ea580c',
  success: '#22c55e',
  error: '#ef4444',
  warning: '#f59e0b',
};

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_CONSUMPTION: MaterialConsumption[] = [
  {
    id: 1,
    material: { id: 1, name: 'Papel A4 80g (Resma)', unit: 'resma', category: 'papel' },
    project: { id: 101, reference: 'PRJ-2026-001', title: 'Catálogos Empresa ABC' },
    quantity: 5,
    consumedAt: '2026-03-20T10:30:00Z',
    consumedBy: 'Carlos López',
    notes: 'Impresión doble cara',
  },
  {
    id: 2,
    material: { id: 3, name: 'Tinta Cyan Epson 1L', unit: 'litro', category: 'tinta' },
    project: { id: 101, reference: 'PRJ-2026-001', title: 'Catálogos Empresa ABC' },
    quantity: 2,
    consumedAt: '2026-03-20T11:00:00Z',
    consumedBy: 'Carlos López',
  },
  {
    id: 3,
    material: { id: 6, name: 'Film Transfer PET 30cm', unit: 'metro', category: 'transfer' },
    project: { id: 102, reference: 'PRJ-2026-002', title: 'Camisetas DTF Evento' },
    quantity: 50,
    consumedAt: '2026-03-21T09:15:00Z',
    consumedBy: 'Ana García',
    notes: 'Pedido urgente',
  },
  {
    id: 4,
    material: { id: 4, name: 'Tinta Magenta Epson 1L', unit: 'litro', category: 'tinta' },
    project: { id: 102, reference: 'PRJ-2026-002', title: 'Camisetas DTF Evento' },
    quantity: 1,
    consumedAt: '2026-03-21T09:30:00Z',
    consumedBy: 'Ana García',
  },
  {
    id: 5,
    material: { id: 8, name: 'Caja Envío Pequeña', unit: 'unidad', category: 'embalaje' },
    project: { id: 103, reference: 'PRJ-2026-003', title: 'Envío Flyers Locales' },
    quantity: 20,
    consumedAt: '2026-03-22T14:00:00Z',
    consumedBy: 'María Torres',
  },
  {
    id: 6,
    material: { id: 1, name: 'Papel A4 80g (Resma)', unit: 'resma', category: 'papel' },
    project: { id: 104, reference: 'PRJ-2026-004', title: 'Dípticos Farmacia Norte' },
    quantity: 3,
    consumedAt: '2026-03-23T08:45:00Z',
    consumedBy: 'Carlos López',
    notes: 'Material reservado',
  },
  {
    id: 7,
    material: { id: 7, name: 'Film Transfer PET 60cm', unit: 'metro', category: 'transfer' },
    project: { id: 105, reference: 'PRJ-2026-005', title: 'Lonas Publicidad Exterior' },
    quantity: 30,
    consumedAt: '2026-03-24T10:00:00Z',
    consumedBy: 'Pedro Ruiz',
  },
  {
    id: 8,
    material: { id: 5, name: 'Tinta Amarilla Epson 1L', unit: 'litro', category: 'tinta' },
    project: { id: 105, reference: 'PRJ-2026-005', title: 'Lonas Publicidad Exterior' },
    quantity: 1,
    consumedAt: '2026-03-24T10:20:00Z',
    consumedBy: 'Pedro Ruiz',
    notes: 'Última unidad disponible',
  },
];

// ─── Category colors ──────────────────────────────────────────────────────────

const CAT_COLORS: Record<string, string> = {
  papel: '#3b82f6',
  tinta: '#8b5cf6',
  transfer: '#f97316',
  embalaje: '#22c55e',
  otro: '#6b7280',
};

const CAT_LABELS: Record<string, string> = {
  papel: 'Papel',
  tinta: 'Tinta',
  transfer: 'Transfer',
  embalaje: 'Embalaje',
  otro: 'Otro',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function CatBadge({ cat }: { cat: string }) {
  const color = CAT_COLORS[cat] || CAT_COLORS.otro;
  const label = CAT_LABELS[cat] || cat;
  return (
    <span style={{
      padding: '3px 9px', borderRadius: 20,
      background: `${color}18`, border: `1px solid ${color}40`,
      color, fontSize: 11, fontWeight: 600,
      fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

// ─── Summary card ─────────────────────────────────────────────────────────────

function SummaryCard({ icon: Icon, label, value, color, bg, border }: {
  icon: React.ElementType;
  label: string;
  value: string;
  color: string;
  bg: string;
  border: string;
}) {
  return (
    <div style={{ padding: '20px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 11,
          background: bg, border: `1px solid ${border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon size={18} color={color} />
        </div>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color, fontFamily: 'Syne, sans-serif', letterSpacing: '-0.02em' }}>{value}</div>
          <div style={{ fontSize: 12, color: C.textSecondary }}>{label}</div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function MaterialConsumptionPage() {
  const [searchMaterial, setSearchMaterial] = useState('');
  const [searchProject, setSearchProject] = useState('');
  const [filterCat, setFilterCat] = useState<string>('all');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const { data: apiData, isLoading } = useQuery({
    queryKey: ['material-consumption'],
    queryFn: () => materialConsumptionApi.list(),
    retry: false,
  });

  const records: MaterialConsumption[] = (Array.isArray(apiData) && apiData.length > 0)
    ? apiData
    : MOCK_CONSUMPTION;

  const filtered = useMemo(() => {
    let list = [...records];

    if (searchMaterial.trim()) {
      const q = searchMaterial.toLowerCase();
      list = list.filter(r => r.material.name.toLowerCase().includes(q));
    }

    if (searchProject.trim()) {
      const q = searchProject.toLowerCase();
      list = list.filter(r =>
        r.project.title.toLowerCase().includes(q) ||
        r.project.reference.toLowerCase().includes(q)
      );
    }

    if (filterCat !== 'all') {
      list = list.filter(r => r.material.category === filterCat);
    }

    return list.sort((a, b) => new Date(b.consumedAt).getTime() - new Date(a.consumedAt).getTime());
  }, [records, searchMaterial, searchProject, filterCat]);

  const totalQuantity = filtered.reduce((sum, r) => sum + r.quantity, 0);
  const uniqueMaterials = new Set(filtered.map(r => r.material.id)).size;
  const uniqueProjects = new Set(filtered.map(r => r.project.id)).size;

  const categories = Array.from(new Set(records.map(r => r.material.category)));

  const inputStyle: React.CSSProperties = {
    background: 'transparent', border: 'none', outline: 'none',
    color: '#fff', fontSize: 13, width: '100%',
    fontFamily: 'DM Sans, sans-serif',
  };

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '32px', minHeight: '100vh', background: C.bg, fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 9,
            background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <History size={16} color={C.orange} />
          </div>
          <h1 style={{
            fontFamily: 'Syne, sans-serif', fontWeight: 900,
            fontSize: isMobile ? 22 : 26, color: '#fff',
            margin: 0, letterSpacing: '-0.03em',
          }}>
            Historial de Consumo
          </h1>
        </div>
        <p style={{ fontSize: 13, color: C.textSecondary, margin: 0, paddingLeft: 48 }}>
          Trazabilidad de materiales consumidos por proyecto
        </p>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        <SummaryCard
          icon={TrendingDown}
          label="Consumos filtrados"
          value={String(filtered.length)}
          color={C.orange}
          bg="rgba(249,115,22,0.1)"
          border="rgba(249,115,22,0.25)"
        />
        <SummaryCard
          icon={Package}
          label="Materiales distintos"
          value={String(uniqueMaterials)}
          color="#a78bfa"
          bg="rgba(167,139,250,0.08)"
          border="rgba(167,139,250,0.25)"
        />
        <SummaryCard
          icon={FolderKanban}
          label="Proyectos afectados"
          value={String(uniqueProjects)}
          color={C.success}
          bg="rgba(34,197,94,0.08)"
          border="rgba(34,197,94,0.25)"
        />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10, marginBottom: 20, alignItems: isMobile ? 'stretch' : 'center', flexWrap: 'wrap' }}>
        {/* Material search */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          flex: '1 1 200px', background: C.card,
          border: `1px solid ${C.border}`, borderRadius: 9, padding: '9px 14px',
        }}>
          <Package size={14} color={C.textSecondary} />
          <input
            type="text"
            placeholder="Buscar material..."
            value={searchMaterial}
            onChange={e => setSearchMaterial(e.target.value)}
            style={inputStyle}
          />
          {searchMaterial && (
            <button onClick={() => setSearchMaterial('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex', padding: 0 }}>
              <X size={13} />
            </button>
          )}
        </div>

        {/* Project search */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          flex: '1 1 200px', background: C.card,
          border: `1px solid ${C.border}`, borderRadius: 9, padding: '9px 14px',
        }}>
          <Search size={14} color={C.textSecondary} />
          <input
            type="text"
            placeholder="Buscar proyecto..."
            value={searchProject}
            onChange={e => setSearchProject(e.target.value)}
            style={inputStyle}
          />
          {searchProject && (
            <button onClick={() => setSearchProject('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex', padding: 0 }}>
              <X size={13} />
            </button>
          )}
        </div>

        {/* Category filter chips — scrollable on mobile */}
        <div style={{ display: 'flex', gap: 6, flexWrap: isMobile ? 'nowrap' : 'wrap', overflowX: isMobile ? 'auto' : undefined, paddingBottom: isMobile ? 4 : 0, alignItems: 'center' }}>
          <Filter size={13} color={C.textMuted} style={{ flexShrink: 0 }} />
          {([['all', 'Todos', C.textSecondary], ...categories.map(c => [c, CAT_LABELS[c] || c, CAT_COLORS[c] || CAT_COLORS.otro])] as [string, string, string][]).map(([val, label, color]) => {
            const active = filterCat === val;
            return (
              <button
                key={val}
                onClick={() => setFilterCat(val)}
                style={{
                  padding: '7px 14px', borderRadius: 8, whiteSpace: 'nowrap',
                  border: active ? `1px solid ${color}60` : `1px solid ${C.border}`,
                  background: active ? `${color}18` : C.card,
                  color: active ? color : C.textSecondary,
                  fontSize: 12, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                  fontWeight: active ? 600 : 400, transition: 'all 0.15s',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Table / Cards */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div style={{ width: 32, height: 32, border: `3px solid ${C.border}`, borderTopColor: C.orange, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : isMobile ? (
        /* Mobile: card view */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(record => (
            <div key={record.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 16px' }}>
              {/* Material */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary }}>{record.material.name}</span>
                    <CatBadge cat={record.material.category} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FolderKanban size={11} color={C.textMuted} />
                    <span style={{ fontSize: 11, color: C.textSecondary }}>{record.project.reference}</span>
                    <span style={{ fontSize: 11, color: C.textMuted }}>·</span>
                    <span style={{ fontSize: 11, color: C.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record.project.title}</span>
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: C.orange, fontFamily: 'Syne, sans-serif' }}>
                    {record.quantity}
                  </div>
                  <div style={{ fontSize: 10, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace' }}>{record.material.unit}</div>
                </div>
              </div>

              {/* Footer */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 11, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace' }}>
                  {formatDate(record.consumedAt)}
                </span>
                {record.consumedBy && (
                  <span style={{ fontSize: 11, color: C.textSecondary }}>{record.consumedBy}</span>
                )}
              </div>
              {record.notes && (
                <div style={{ marginTop: 8, padding: '6px 10px', background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.15)', borderRadius: 6, fontSize: 11, color: C.textSecondary }}>
                  {record.notes}
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: '48px 16px', textAlign: 'center', color: C.textMuted, fontSize: 14 }}>
              No se encontraron registros de consumo
            </div>
          )}
        </div>
      ) : (
        /* Desktop: table view */
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {['Material', 'Categoría', 'Proyecto', 'Cantidad', 'Fecha', 'Consumido por'].map(h => (
                  <th key={h} style={{
                    padding: '12px 16px', textAlign: 'left',
                    fontSize: 11, color: C.textMuted,
                    fontFamily: 'JetBrains Mono, monospace',
                    letterSpacing: '0.06em', fontWeight: 600,
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(record => (
                <tr
                  key={record.id}
                  style={{ transition: 'background 0.1s', background: 'transparent' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  {/* Material */}
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid #1a1a28` }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary }}>{record.material.name}</div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>por {record.material.unit}</div>
                  </td>

                  {/* Category */}
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid #1a1a28` }}>
                    <CatBadge cat={record.material.category} />
                  </td>

                  {/* Project */}
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid #1a1a28`, maxWidth: 220 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.textPrimary, fontFamily: 'JetBrains Mono, monospace' }}>
                      {record.project.reference}
                    </div>
                    <div style={{ fontSize: 11, color: C.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {record.project.title}
                    </div>
                  </td>

                  {/* Quantity */}
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid #1a1a28` }}>
                    <span style={{
                      fontSize: 15, fontWeight: 800, color: C.orange,
                      fontFamily: 'Syne, sans-serif', letterSpacing: '-0.01em',
                    }}>
                      {record.quantity}
                    </span>
                  </td>

                  {/* Date */}
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid #1a1a28` }}>
                    <span style={{ fontSize: 12, color: C.textSecondary, fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'nowrap' }}>
                      {formatDate(record.consumedAt)}
                    </span>
                  </td>

                  {/* Consumed by */}
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid #1a1a28` }}>
                    <div>
                      <span style={{ fontSize: 12, color: record.consumedBy ? C.textPrimary : C.textMuted }}>
                        {record.consumedBy || '—'}
                      </span>
                      {record.notes && (
                        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={record.notes}>
                          {record.notes}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '48px 16px', textAlign: 'center', color: C.textMuted, fontSize: 14 }}>
                    No se encontraron registros de consumo
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
